import { authenticatedFetch } from "@lumi/data";
import { useCallback, useMemo } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import { POLLING_INTERVALS, usePolling, type UsePollingOptions } from "./usePolling";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "http://localhost:3333";

/**
 * Encoding status values from the database
 */
export type EncodingStatus = "PENDING" | "UPLOADING" | "UPLOADED" | "ENCODING" | "READY" | "ERRORED";

/**
 * Encoding entry representing a submission asset being encoded
 */
export interface EncodingEntry {
  id: string;
  submissionId: string;
  submissionTitle: string;
  kind: "FEATURE" | "TRAILER";
  rawFileName: string | null;
  rawFileSize: string | null; // BigInt serialized as string
  encodingStatus: EncodingStatus;
  encodingError: string | null;
  encodingTier: string | null;
  streamVideoId: string | null;
  hlsUrl: string | null;
  uploadStartedAt: string | null;
  uploadCompletedAt: string | null;
  durationSec: number | null;
  resolution: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Encoding queue statistics
 */
export interface EncodingStats {
  /** Total items currently in queue (PENDING, UPLOADED, ENCODING) */
  queueLength: number;
  /** Number of items actively encoding */
  activeEncoding: number;
  /** Number of items waiting to encode */
  pending: number;
  /** Number of items that have uploaded but not yet encoding */
  uploaded: number;
  /** Number of completed items (today) */
  completedToday: number;
  /** Number of failed items (all time) */
  failedTotal: number;
  /** Average encoding time in seconds (last 7 days) */
  avgEncodingTimeSec: number | null;
  /** Success rate percentage (last 7 days) */
  successRatePercent: number | null;
}

/**
 * Encoding dashboard data from API
 */
export interface EncodingDashboardData {
  stats: EncodingStats;
  active: EncodingEntry[];
  failed: EncodingEntry[];
  recentlyCompleted: EncodingEntry[];
}

/**
 * Result type for useEncodingStatus hook
 */
export interface UseEncodingStatusResult {
  /** Dashboard data (stats + entries) */
  data: EncodingDashboardData | null;
  /** Loading state */
  loading: boolean;
  /** Error message */
  error: string | null;
  /** Whether polling is active */
  isPolling: boolean;
  /** Whether polling is paused */
  isPaused: boolean;
  /** Number of consecutive errors */
  consecutiveErrors: number;
  /** Manual refresh function */
  refresh: () => Promise<void>;
  /** Pause polling */
  pause: () => void;
  /** Resume polling */
  resume: () => void;
  /** Reset error state and resume */
  reset: () => void;
  /** Retry a failed encoding */
  retryEncoding: (assetId: string) => Promise<boolean>;
  /** Cancel an encoding (if supported) */
  cancelEncoding: (assetId: string) => Promise<boolean>;
}

/**
 * Hook for fetching and polling encoding status for the encoding dashboard
 *
 * Polls the encoding stats and active/failed/completed lists at 15s intervals
 * with automatic backoff on errors and pause when tab is hidden.
 *
 * @example
 * ```tsx
 * const { data, loading, error, refresh, retryEncoding } = useEncodingStatus();
 *
 * if (loading) return <Spinner />;
 * if (error) return <ErrorMessage>{error}</ErrorMessage>;
 *
 * return (
 *   <div>
 *     <StatsPanel stats={data.stats} />
 *     <ActiveEncodings entries={data.active} />
 *     <FailedEncodings entries={data.failed} onRetry={retryEncoding} />
 *   </div>
 * );
 * ```
 */
export function useEncodingStatus(
  options: Omit<UsePollingOptions, "interval"> & { interval?: number } = {},
): UseEncodingStatusResult {
  const { accessToken, refreshAuth } = useAuth();

  /**
   * Fetch encoding dashboard data from the dedicated API endpoint
   */
  const fetchEncodingData = useCallback(async (): Promise<EncodingDashboardData> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    // Use the dedicated encoding stats endpoint
    const data = await authenticatedFetch<EncodingDashboardData>(
      `${API_BASE}/v1/admin/encoding/stats`,
      { method: "GET" },
      accessToken,
      createRefreshCallback(refreshAuth),
    );

    return data;
  }, [accessToken, refreshAuth]);

  // Use the generic polling hook
  const pollingResult = usePolling(fetchEncodingData, {
    interval: POLLING_INTERVALS.ACTIVE_QUEUE,
    enabled: !!accessToken,
    ...options,
  });

  /**
   * Retry a failed encoding
   */
  const retryEncoding = useCallback(
    async (assetId: string): Promise<boolean> => {
      if (!accessToken) return false;

      // Find the submission ID for this asset
      const entry = pollingResult.data?.failed.find((e) => e.id === assetId);
      if (!entry) return false;

      try {
        await authenticatedFetch(
          `${API_BASE}/v1/admin/submissions/${entry.submissionId}/trigger-encoding`,
          { method: "POST" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        // Refresh the data after retry
        await pollingResult.refresh();
        return true;
      } catch (error) {
        console.error("Failed to retry encoding:", error);
        return false;
      }
    },
    [accessToken, refreshAuth, pollingResult],
  );

  /**
   * Cancel an encoding (placeholder - may not be supported by Cloudflare Stream)
   */
  const cancelEncoding = useCallback((_assetId: string): Promise<boolean> => {
    // Cloudflare Stream doesn't support canceling encodings
    // This is a placeholder for future implementation if needed
    console.warn("Cancel encoding not currently supported");
    return Promise.resolve(false);
  }, []);

  return useMemo(
    () => ({
      data: pollingResult.data,
      loading: pollingResult.loading,
      error: pollingResult.error,
      isPolling: pollingResult.isPolling,
      isPaused: pollingResult.isPaused,
      consecutiveErrors: pollingResult.consecutiveErrors,
      refresh: pollingResult.refresh,
      pause: pollingResult.pause,
      resume: pollingResult.resume,
      reset: pollingResult.reset,
      retryEncoding,
      cancelEncoding,
    }),
    [pollingResult, retryEncoding, cancelEncoding],
  );
}

/**
 * Hook for checking if any encoding is in progress
 * Useful for showing indicators in navigation/headers
 */
export function useHasActiveEncoding(options: { enabled?: boolean } = {}): {
  hasActive: boolean;
  activeCount: number;
  loading: boolean;
} {
  const { data, loading } = useEncodingStatus({
    enabled: options.enabled ?? true,
    interval: POLLING_INTERVALS.SLOW, // Use slower interval for this indicator
  });

  return useMemo(
    () => ({
      hasActive: (data?.stats.queueLength ?? 0) > 0,
      activeCount: data?.stats.queueLength ?? 0,
      loading,
    }),
    [data, loading],
  );
}
